import { KeyboardEvent } from 'react';
import { ArrowUp, Square } from 'lucide-react';

interface Props {
  value: string;
  onChange: (text: string) => void;
  /** A reply is streaming: the send button becomes Stop. */
  streaming: boolean;
  disabled: boolean;
  onSend: () => void;
  onStop: () => void;
}

const MAX = 4000;
const SOFT_LIMIT = 3500;

/** Enter sends, Shift+Enter starts a new line; while the coach answers, Stop. */
export default function CoachComposer({ value, onChange, streaming, disabled, onSend, onStop }: Props) {
  function trySend() {
    if (!value.trim() || disabled || streaming) return;
    onSend();
  }

  function handleKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      trySend();
    }
  }

  return (
    <div className="border-t border-line p-3">
      <div className="flex items-end gap-2 rounded-[16px] border border-line-strong bg-surface px-3 py-2 focus-within:ring-2 focus-within:ring-accent">
        <textarea
          data-testid="coach-composer-input"
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, MAX))}
          onKeyDown={handleKey}
          rows={2}
          disabled={disabled}
          aria-label="Ask the coach"
          placeholder="Ask the coach… (Enter to send, Shift+Enter for a new line)"
          className="max-h-40 min-h-[44px] flex-1 resize-none border-0 bg-transparent p-0 text-sm text-ink focus:ring-0"
        />
        {streaming ? (
          <button type="button" onClick={onStop} aria-label="Stop the answer" title="Stop"
            className="grid h-9 w-9 flex-none place-items-center rounded-full border border-line-strong text-ink hover:bg-surface-2">
            <Square size={14} fill="currentColor" aria-hidden />
          </button>
        ) : (
          <button type="button" disabled={disabled || !value.trim()} onClick={trySend} aria-label="Send" title="Send"
            className="grid h-9 w-9 flex-none place-items-center rounded-full bg-navy text-white disabled:opacity-40 dark:bg-mist-fixed dark:text-navy">
            <ArrowUp size={18} aria-hidden />
          </button>
        )}
      </div>
      {value.length >= SOFT_LIMIT && <p className="mt-1 text-right text-xs text-warning-ink">{value.length}/{MAX}</p>}
    </div>
  );
}
